<template>
    <div class="modal-body modal-height-max" style="overflow-y: auto;">
        <div class="card">
            <div class="card-body">
                <p class="mb-3">{{ t('PROPOSAL.PUBLIC_LINK.DESCRIPTION') }}</p>
                <p class="mb-3" v-if="reserve?.realtor?.name">
                    <strong>{{ t('PROPOSAL.PUBLIC_LINK.REALTOR') }}:</strong> {{ reserve.realtor.name }}
                </p>
                <div class="d-flex flex-wrap">
                    <button class="btn btn-custom bg-btn mr-2 mb-2" type="button" :disabled="loading"
                            data-cy="public_link_generate_send" @click="generate(true)">
                        <i class="icon-send mr-1"></i>{{ t('PROPOSAL.PUBLIC_LINK.GENERATE_SEND') }}
                    </button>
                    <button class="btn btn-secondary mb-2" type="button" :disabled="loading"
                            data-cy="public_link_generate" @click="generate(false)">
                        <i class="icon-link mr-1"></i>{{ t('PROPOSAL.PUBLIC_LINK.GENERATE') }}
                    </button>
                </div>
                <div class="form-group mt-2" v-if="generated">
                    <label>Link ({{ t('PROPOSAL.PUBLIC_LINK.EXPIRES_AT') }}: {{ formatDate(generated.expires_at) }})</label>
                    <div class="input-group">
                        <input class="form-control" type="text" readonly :value="generated.link"
                               data-cy="public_link_value">
                        <div class="input-group-append">
                            <button class="btn btn-secondary" type="button" @click="copyLink(generated.link)">
                                {{ t('PROPOSAL.PUBLIC_LINK.COPY') }}
                            </button>
                            <a class="btn btn-success" target="_blank" rel="noopener" :href="whatsappShareUrl">
                                {{ t('PROPOSAL.PUBLIC_LINK.SHARE_WHATSAPP') }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="card mt-3">
            <div class="card-header"><strong>{{ t('PROPOSAL.PUBLIC_LINK.HISTORY') }}</strong></div>
            <div class="card-body">
                <p v-if="!links.length" class="text-center mb-0">{{ t('PROPOSAL.PUBLIC_LINK.EMPTY') }}</p>
                <div class="table-responsive" v-else>
                    <table class="table table-hover table-bordered">
                        <thead>
                        <tr>
                            <th class="text-center">{{ t('PROPOSAL.PUBLIC_LINK.STATUS') }}</th>
                            <th class="text-center">{{ t('PROPOSAL.PUBLIC_LINK.REALTOR') }}</th>
                            <th class="text-center">{{ t('PROPOSAL.PUBLIC_LINK.CREATED_BY') }}</th>
                            <th class="text-center">{{ t('PROPOSAL.PUBLIC_LINK.CREATED_AT') }}</th>
                            <th class="text-center">{{ t('PROPOSAL.PUBLIC_LINK.EXPIRES_AT') }}</th>
                            <th class="text-center">{{ t('PROPOSAL.PUBLIC_LINK.USED_AT') }}</th>
                            <th class="text-center"></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="link in links" :key="link.id">
                            <td class="text-center">
                                <span class="badge" :class="statusClass(link.status)">{{ link.status }}</span>
                            </td>
                            <td class="text-center">{{ link.realtor ?? '-' }}</td>
                            <td class="text-center">{{ link.created_by ?? '-' }}</td>
                            <td class="text-center">{{ formatDate(link.created_at) }}</td>
                            <td class="text-center">{{ formatDate(link.expires_at) }}</td>
                            <td class="text-center">{{ formatDate(link.used_at) }}</td>
                            <td class="text-center text-nowrap">
                                <button v-if="link.status === 'ATIVO' && link.link" class="btn btn-sm btn-secondary mr-1"
                                        type="button" data-cy="public_link_copy_row" @click="copyLink(link.link)">
                                    {{ t('PROPOSAL.PUBLIC_LINK.COPY') }}
                                </button>
                                <button v-if="link.status === 'ATIVO'" class="btn btn-sm btn-danger" type="button"
                                        :disabled="loading" @click="revoke(link)">
                                    {{ t('PROPOSAL.PUBLIC_LINK.REVOKE') }}
                                </button>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {useI18n} from 'vue-i18n';
import {useToast} from 'vue-toastification';
import Reserve from '@/services/Reserves';
import errorMsg from '@/components/errorMsg';

export default {
    name: 'PublicProposalLinkComponent',
    mixins: [errorMsg],
    props: {
        reserve: {type: Object, required: true},
    },
    setup() {
        const toast = useToast();
        const {t} = useI18n();
        return {t, toast};
    },
    data() {
        return {
            links: [],
            generated: null,
            loading: false,
        };
    },
    computed: {
        allotmentId() {
            return this.reserve?.properties?.allotment_id;
        },
        whatsappShareUrl() {
            const text = `Segue o link para lançar a proposta da reserva: ${this.generated?.link}`;
            return `https://wa.me/?text=${encodeURIComponent(text)}`;
        },
    },
    mounted() {
        this.listLinks();
    },
    methods: {
        listLinks() {
            Reserve.listProposalLinks(this.reserve.id, this.allotmentId)
                .then(resp => this.links = resp.data)
                .catch(error => this.errorMsg(error));
        },
        generate(sendWhatsapp) {
            this.loading = true;
            Reserve.generateProposalLink(this.reserve.id, sendWhatsapp, this.allotmentId).then(resp => {
                this.generated = resp.data;
                this.toast.success(this.t('PROPOSAL.PUBLIC_LINK.GENERATED'));
                if (sendWhatsapp) {
                    resp.data.whatsapp_sent
                        ? this.toast.success(this.t('PROPOSAL.PUBLIC_LINK.SENT'))
                        : this.toast.warning(this.t('PROPOSAL.PUBLIC_LINK.NOT_SENT'), {timeout: 8000});
                }
                this.listLinks();
            }).catch(error => this.errorMsg(error)).finally(() => this.loading = false);
        },
        revoke(link) {
            this.loading = true;
            Reserve.revokeProposalLink(link.id, this.allotmentId).then(() => {
                this.toast.success(this.t('PROPOSAL.PUBLIC_LINK.REVOKED'));
                if (this.generated?.id === link.id) this.generated = null;
                this.listLinks();
            }).catch(error => this.errorMsg(error)).finally(() => this.loading = false);
        },
        copyLink(text) {
            // navigator.clipboard so existe em contexto seguro (https/localhost); fallback para acesso por IP.
            if (navigator.clipboard) {
                navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                this.$el.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                this.$el.removeChild(textarea);
            }
            this.toast.success(this.t('PROPOSAL.PUBLIC_LINK.COPIED'));
        },
        formatDate(value) {
            if (!value) return '-';
            const [date, time] = value.split(' ');
            const [year, month, day] = date.split('-');
            return `${day}/${month}/${year} ${time?.substring(0, 5) ?? ''}`;
        },
        statusClass(status) {
            return {
                'ATIVO': 'badge-success',
                'UTILIZADO': 'badge-info',
                'REVOGADO': 'badge-secondary',
                'EXPIRADO': 'badge-warning',
            }[status] ?? 'badge-danger';
        },
    },
};
</script>
